import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  conversionsKeys,
  creditsKeys,
  dataKeys,
  DEFAULT_FILTERS,
} from "./conversions.queries";
import { api } from "@/lib/axios";
import { Batch } from "@/types/dashboard.types";

export const useConversions = () => {
  return useInfiniteQuery({
    queryKey: conversionsKeys.all,
    queryFn: async ({ pageParam = 0 }) => {
      try {
        const response = await api.get<{
          batches: Batch[];
          count: number;
        }>("/api/user/conversions", {
          params: {
            page: pageParam,
            pageSize: DEFAULT_FILTERS.pageSize,
          },
        });

        if (!response.data) {
          throw new Error("No data returned from API");
        }

        return response.data;
      } catch (error) {
        throw new Error("Failed to fetch conversions");
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      const hasMore = lastPage.count > allPages.length * 7;
      return hasMore ? allPages.length : undefined;
    },
    initialPageParam: 0,
  });
};

export const useConversion = (id: string) => {
  return useQuery({
    queryKey: conversionsKeys.detail(id),
    queryFn: async () => {
      try {
        const response = await api.get<Batch>(`/api/user/conversions/${id}`);

        return response.data;
      } catch (e) {
        throw new Error(`Failed to fetch conversion details ${id}`);
      }
    },
  });
};

export const useCredits = () => {
  return useQuery({
    queryKey: creditsKeys,
    queryFn: async () => {
      try {
        const response = await api.get<number>("/api/user/credits");

        return response.data;
      } catch (error) {
        throw new Error("Failed to fetch credits");
      }
    },
  });
};

export const useCountries = () => {
  return useQuery({
    queryKey: dataKeys.countries,
    queryFn: async () => {
      try {
        const response = await api.get<Array<{ id: string; name: string }>>(
          "/api/conversion/countries"
        );
        return response.data;
      } catch (error) {
        console.log(error);
        return [];
      }
    },
  });
};

export const useGradingScales = (countryId: string | null) => {
  return useQuery({
    queryKey: [...dataKeys.gradingScales, countryId],
    queryFn: async () => {
      if (!countryId) {
        return [];
      }
      try {
        const response = await api.get<
          Array<{
            id: string;
            name: string;
            university: string;
            scale: unknown;
          }>
        >(`/api/conversion/countries/${countryId}/grading-scales`);
        return response.data;
      } catch (error) {
        console.error("Failed to fetch grading scales:", error);
        return [];
      }
    },
    enabled: !!countryId, // Only run the query if countryId is available
  });
};
